import {
  siAlmalinux,
  siArchlinux,
  siCentos,
  siDebian,
  siFedora,
  siFreebsd,
  siOpensuse,
  siRockylinux,
  siUbuntu,
} from "simple-icons";

/*
 * Operating systems of servers and images, for `OsIcon`. Logos come from
 * Simple Icons (CC0-1.0; the marks stay their owners' trademarks and only
 * identify the system). Simple Icons has no Windows logo: Windows and
 * unknown systems get glyphs of our own.
 */

export interface OsInfo {
  /** Our key: `ubuntu`, `rocky`, … or `windows` / `other`. */
  key: string;
  /** Name for people and screen readers, e.g. "Rocky Linux". */
  name: string;
  /** SVG path in a 24×24 box. */
  path: string;
  /** Brand colour, `#rrggbb`. */
  color: string;
  /** Drawn with even-odd filling (our own glyphs with holes). */
  evenOdd?: boolean;
}

const fromSimpleIcon = (key: string, icon: { title: string; path: string; hex: string }) => ({
  key,
  name: icon.title,
  path: icon.path,
  color: `#${icon.hex}`,
});

/** Our own: four panes of a window. */
const WINDOWS: OsInfo = {
  key: "windows",
  name: "Windows",
  path: "M3 3h8.4v8.4H3zM12.6 3H21v8.4h-8.4zM3 12.6h8.4V21H3zM12.6 12.6H21V21h-8.4z",
  color: "#0078d4",
};

/** Our own: a server with two units. */
export const OTHER_OS: OsInfo = {
  key: "other",
  name: "",
  path: "M4 3h16a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm2 3v2h2V6zM4 13h16a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1zm2 3v2h2v-2z",
  color: "#64748b",
  evenOdd: true,
};

const KNOWN: OsInfo[] = [
  fromSimpleIcon("ubuntu", siUbuntu),
  fromSimpleIcon("debian", siDebian),
  fromSimpleIcon("fedora", siFedora),
  fromSimpleIcon("rocky", siRockylinux),
  fromSimpleIcon("alma", siAlmalinux),
  fromSimpleIcon("centos", siCentos),
  fromSimpleIcon("opensuse", siOpensuse),
  fromSimpleIcon("arch", siArchlinux),
  fromSimpleIcon("freebsd", siFreebsd),
  WINDOWS,
];

/** Other spellings of the families (Hetzner writes `rocky`, `alma`; others the full name). */
const ALIASES: Record<string, string> = {
  rockylinux: "rocky",
  almalinux: "alma",
  archlinux: "arch",
  suse: "opensuse",
  "opensuse-leap": "opensuse",
  win: "windows",
};

/**
 * The OS of a provider's `osFlavor` (any case), or of an image description
 * when the flavour is `unknown` (snapshots: "My Ubuntu backup").
 */
export function osInfo(flavor: string | null | undefined, description?: string | null): OsInfo {
  const key = (flavor ?? "").trim().toLowerCase();
  const found = KNOWN.find((os) => os.key === (ALIASES[key] ?? key));
  if (found) return found;
  const text = (description ?? "").toLowerCase();
  // Whole words only: "arch" must not match "search" or "architecture".
  const word = (w: string) => new RegExp(`(^|[^a-z])${w}([^a-z]|$)`).test(text);
  const named = KNOWN.find(
    (os) => (os.key !== "arch" && word(os.key)) || word(os.name.toLowerCase()),
  );
  if (named) return named;
  return { ...OTHER_OS, name: flavor && key !== "unknown" ? flavor : "" };
}

/**
 * Whether a server runs Windows: its image says so (Hetzner has no ready-made Windows
 * images, but snapshots of an installed one are named), or the person
 * marked it with the label `os=windows`. The picker offers the remote
 * desktop for these and SSH otherwise.
 */
export function isWindowsServer(server: {
  image?: { osFlavor?: string | null; description?: string | null } | null;
  labels?: Record<string, string>;
}): boolean {
  if (server.labels?.os?.trim().toLowerCase() === "windows") return true;
  return osInfo(server.image?.osFlavor, server.image?.description).key === "windows";
}
